import { Injectable } from "@angular/core";
import { ValorizationService } from "../../valorization/valorization.service";
import { PatientService } from "../../patient.service";
import { PatientView } from "../../../model/patient/patient-view";
import { AdmissionService } from "../../admission.service";
import {
  Admission,
  AdmissionValorization,
  AdmissionMedicalOrder,
  ValorizationValidation,
} from "../../../model/admission";
import { MedicalOrderOperation } from "../../../model/medical-order-operation";
import { Router } from "@angular/router";
import { InvoicePatientCashDeskService } from "../../invoice-cash-desk/invoice-patient-cash-desk.service";
import { CashDeskUserOpen } from "../../../model/cash-desk-user-open";
import { MedicalAppointmentValidation } from "../../../model/medical-appointment-validation";
import { AuthService } from "../../auth.service";
import { ConfirmDialogService } from "../../confirm-dialog.service";
import { OnLineValidationService } from "../../on-line-validation/on-line-validation.service";
import { MedicalOrderService } from "../../medical-order.service";
import { FilterService } from "../../filter.service";
import { BudgetService } from "../../budget/budget.service";
import { MatDialog } from "@angular/material";
import { BudgetListDialogComponent } from "src/app/components/dialogs/budget-list-dialog/budget-list-dialog.component";
import { BudgetFilter } from "src/app/model/budget/budget-filter";

@Injectable()
export abstract class AdmissionWizardService {
  constructor(
    public valorizationService: ValorizationService,
    private filterService: FilterService,
    public admissionService: AdmissionService,
    public medicalOrderService: MedicalOrderService,
    private onLineValidationService: OnLineValidationService,
    public router: Router,
    public confirmDialogService: ConfirmDialogService,
    public authService: AuthService,
    public invoiceCashDeskService: InvoicePatientCashDeskService,
    public patientService: PatientService,
    public budgetService: BudgetService,
    public dialog: MatDialog
  ) {}

  public admission: Admission;
  public medicalOrderOperation: MedicalOrderOperation;
  public patient: PatientView;
  public cashDeskUserOpen: CashDeskUserOpen;

  initialize(admission: Admission) {
    this.reset();
    this.admission = admission;

    this.invoiceCashDeskService
      .getCashDeskUserOpen(
        false,
        false,
        this.admission.MedicalAppointment.EquipmentId
      )
      .then((cashDeskUserOpen) => {
        this.cashDeskUserOpen = cashDeskUserOpen;
      });
  }

  get MedicalAppointmentId(): number {
    return this.admission.MedicalAppointment.Id;
  }

  existsCurrentInvoice(): Promise<boolean> {
    return this.invoiceCashDeskService.existsCurrentInvoice(
      this.admission.MedicalAppointment.Id
    );
  }

  reset(): void {
    this.patient = null;
    this.medicalOrderOperation = null;
    this.admission = null;
  }

  // TODO: unificar esto con lo de admisión [CODIGO REPETIDO]
  async executeOnLineOperation(): Promise<MedicalAppointmentValidation> {
    const medicalAppointmentValidation =
      await this.onLineValidationService.executeOnLineOperation(
        this.admission.MedicalAppointment,
        this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder,
        this.admission.AdmissionValorization.ValorizationEdit
      );

    this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo.MedicalAppointmentValidation =
      medicalAppointmentValidation;

    if (
      !medicalAppointmentValidation.NoValidate &&
      medicalAppointmentValidation.AliquotTax &&
      this.admission.AdmissionValorization.ValorizationEdit.ValorizationAmount
        .Valorization.AliquotTax.Id !==
        medicalAppointmentValidation.AliquotTax.Id
    ) {
      this.admission.AdmissionValorization.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
        medicalAppointmentValidation.AliquotTax;
      this.admission.AdmissionValorization.ValorizationEdit.ValorizationAmount =
        this.valorizationService.recalculatePatientTaxAmount(
          this.admission.AdmissionValorization.ValorizationEdit
            .ValorizationAmount
        );
    }

    return medicalAppointmentValidation;
  }

  async getPatient(): Promise<PatientView> {
    if (!this.patient) {
      this.patient = await this.patientService.getPatientView(
        this.admission.PatientId
      );
    }
    return this.patient;
  }

  setPatient(patient: PatientView): void {
    this.patient = patient;
    this.admission.PatientId = patient.Id;
  }

  async getMedicalOrder(): Promise<AdmissionMedicalOrder> {
    this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo =
      await this.medicalOrderService.medicalOrderEditInfoForAdmission(
        this.admission
      );
    return this.admission.AdmissionMedicalOrder;
  }

  async getOrderSector(): Promise<string> {
    const sectorId =
      this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder
        .SectorId;

    if (sectorId) {
      const sectores = await this.filterService.getSectorsFilter();
      const sector = sectores.find((s) => s.Id == sectorId);
      if (sector != null) {
        return sector.Name;
      }
    }

    return "";
  }

  async getValorizacionValidation(
    agreementPriceIds: number[]
  ): Promise<ValorizationValidation> {
    return await this.valorizationService.getValorizacionValidation(
      this.admission.MedicalAppointment.Id,
      agreementPriceIds
    );
  }

  abstract isOrderInSector(): boolean;
  abstract isWizzard(): boolean;
  abstract isReadOnly(): boolean;
  abstract setReadOnly(): void;
  abstract finishOperations(): void;
  abstract finish();
  abstract recalculateAdmissionValorization(
    admissionValorization: AdmissionValorization
  ): Promise<AdmissionValorization>;
  abstract getAdmissionValorization(): Promise<AdmissionValorization>;
  abstract getMedicalOrderOperation(): MedicalOrderOperation;
  abstract setAdmissionValorization(
    admissionValorization: AdmissionValorization
  );
  abstract setAdmissionMedicalOrder(
    admissionMedicalOrder: AdmissionMedicalOrder
  );

  cancel(): void {
    this.reset();
    history.back();
  }
}

@Injectable()
export class AdmissionReceptionWizardService extends AdmissionWizardService {
  isOrderInSector(): boolean {
    return true;
  }

  finishOperations(): void {
    this.reset();
    this.router.navigate(["admission-reception-search"]);
  }

  isReadOnly(): boolean {
    return false;
  }

  setReadOnly(): void {}

  async finish() {
    this.medicalOrderOperation = await this.admissionService.finishAdmission(
      this.admission
    );

    const valorizationAmount =
      this.admission.AdmissionValorization.ValorizationEdit.ValorizationAmount
        .PatientAmount;
    let orderAmount =
      this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder
        .MedicalOrderAmount;
    if (!orderAmount || orderAmount === 0) {
      orderAmount =
        this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder
          .IncompleteMedicalOrderAmount;
    }

    this.admission = null;
    this.patient = null;

    // si tiene caja asignada y el importe es mayor a cero, redirijo a la cobranza
    if (this.cashDeskUserOpen && (valorizationAmount > 0 || orderAmount > 0)) {
      this.confirmDialogService
        .confirm(
          "Admisión",
          "¿Quiere realizar la cobranza al paciente?",
          "Cobrar",
          "Continuar sín cobrar"
        )
        .subscribe((value: boolean) => {
          if (value) {
            this.router.navigate([
              "admission-invoice-cash-desk",
              this.medicalOrderOperation.MedicalOrderId,
              "admission-reception-search",
              this.medicalOrderOperation.Protocol,
              this.medicalOrderOperation.MedicalApponitmentId,
            ]);
          } else {
            this.admissionMedicalOrderScannNavigate();
          }
        });
    } else if (valorizationAmount > 0 || orderAmount > 0) {
      this.confirmDialogService
        .confirm(
          "Admisión",
          "El paciente debe abonar en caja",
          "Aceptar",
          "Aceptar",
          true
        )
        .subscribe(() => {
          this.admissionMedicalOrderScannNavigate();
        });
    } else {
      this.admissionMedicalOrderScannNavigate();
    }
  }

  private admissionMedicalOrderScannNavigate() {
    this.router.navigate([
      "admission-medical-order-scann",
      this.medicalOrderOperation.MedicalOrderId,
      "admission-reception-search",
      this.medicalOrderOperation.Protocol,
      this.medicalOrderOperation.MedicalApponitmentId,
    ]);
  }

  isWizzard(): boolean {
    return true;
  }

  async recalculateAdmissionValorization(
    admissionValorization: AdmissionValorization
  ): Promise<AdmissionValorization> {
    this.admission.AdmissionValorization =
      await this.valorizationService.recalculateAdmissionValorization(
        admissionValorization
      );
    return await this.getAdmissionValorization();
  }

  async getAdmissionValorization(): Promise<AdmissionValorization> {
    if (!this.admission.AdmissionValorization) {

      if (!this.admission.MedicalAppointment.IsPreAdmission) {
        const budgets = await this.budgetService.getByPatientId(
          this.admission.PatientId
        );

        this.admission.BudgetId = await this.getBudgetId(budgets);

        this.admission.AdmissionValorization =
          await this.valorizationService.getNewAdmissionValorization(
            this.admission.MedicalAppointment.Id,
            this.admission.BudgetId
          );
      } else {
        this.admission.AdmissionValorization =
          await this.valorizationService.getAdmissionValorization(
            this.admission.MedicalAppointment.Id
          );
      }
    }

    return this.admission.AdmissionValorization;
  }

  private async getBudgetId(budgets: BudgetFilter[]) {
    let budgetId: number;
    if (budgets.length > 0) {
      const dialogRef = this.dialog.open(BudgetListDialogComponent, {
        width: "80%",
        data: this.admission.PatientId,
      });

      const budget = await dialogRef.afterClosed().toPromise();

      if (budget) {
        budgetId = budget.Id;
      }
    }
    return budgetId;
  }

  getMedicalOrderOperation(): MedicalOrderOperation {
    return this.medicalOrderOperation;
  }

  setAdmissionValorization(admissionValorization: AdmissionValorization) {
    this.admission.AdmissionValorization = admissionValorization;
  }

  setAdmissionMedicalOrder(admissionMedicalOrder: AdmissionMedicalOrder) {
    this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder.SectorId =
      this.authService.getSectorId();
    this.admission.AdmissionMedicalOrder = admissionMedicalOrder;
  }
}

@Injectable()
export class AdmissionAdmitedWizardService extends AdmissionWizardService {
  private readOnly: boolean;

  isReadOnly(): boolean {
    return this.readOnly;
  }

  isOrderInSector(): boolean {
    const sectorId =
      this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder
        .SectorId;

    if (!this.authService.isReceptionSector()) {
      return false;
    } else if (sectorId == null) {
      return true;
    } else {
      return (
        this.admission.AdmissionMedicalOrder.MedicalOrderEdit.MedicalOrder
          .SectorId === this.authService.getSectorId()
      );
    }
  }

  setReadOnly(): void {
    this.readOnly = true;
  }

  finishOperations(): void {
    this.reset();
    this.router.navigate(["admission-reception-search"]);
  }

  async finish() {
    if (!this.isOrderInSector()) {
      delete this.admission.AdmissionValorization;
    }

    await this.admissionService.admissionSave(this.admission);
    this.router.navigate(["admission-search"]);
  }

  isWizzard(): boolean {
    return false;
  }

  async recalculateAdmissionValorization(
    admissionValorization: AdmissionValorization
  ): Promise<AdmissionValorization> {
    this.admission.AdmissionValorization =
      await this.valorizationService.recalculateAdmissionValorization(
        admissionValorization
      );
    return await this.getAdmissionValorization();
  }

  async getAdmissionValorization(): Promise<AdmissionValorization> {
    if (!this.admission.AdmissionValorization) {
      this.admission.AdmissionValorization =
        await this.valorizationService.getAdmissionValorization(
          this.admission.MedicalAppointment.Id
        );
    }

    return this.admission.AdmissionValorization;
  }

  getMedicalOrderOperation(): MedicalOrderOperation {
    return this.medicalOrderOperation;
  }

  async setAdmissionValorization(admissionValorization: AdmissionValorization) {
    if (this.isOrderInSector()) {
      this.admission.AdmissionValorization = admissionValorization;

      const admissionMedicalOrder = await this.getMedicalOrder();
      admissionMedicalOrder.MedicalOrderEdit.MedicalOrder.IncompleteMedicalOrderAmount =
        admissionMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo.IncompleteMedicalOrderAmount;
      admissionMedicalOrder.MedicalOrderEdit.MedicalOrder.MedicalOrderStateId =
        admissionMedicalOrder.MedicalOrderEdit.MedicalOrderEditInfo.MedicalOrderState.Id;
    }
  }

  setAdmissionMedicalOrder(admissionMedicalOrder: AdmissionMedicalOrder) {
    this.admission.AdmissionMedicalOrder = admissionMedicalOrder;
  }
}

@Injectable()
export class AdmissionPreReceptionWizardService extends AdmissionReceptionWizardService {
  initialize(admission: Admission) {
    this.reset();
    this.admission = admission;
    this.admission.IsPreAdmission = true;
    this.invoiceCashDeskService
      .getCashDeskUserOpen(
        false,
        false,
        this.admission.MedicalAppointment.EquipmentId
      )
      .then((cashDeskUserOpen) => {
        this.cashDeskUserOpen = cashDeskUserOpen;
      });
  }
}
